/**
 * datevSource — shared, per-scope DATEV source loader + mappers (WL-K7).
 *
 * Single source of truth for turning Firestore `invoices`/`expenses` of ONE scope
 * (`userId == scopeUid`) into the decoupled `DatevInvoiceInput`/`DatevExpenseInput`
 * shapes that the pure `buildDatevExport` (`utils/datevExport.ts`) consumes.
 *
 * WHY THIS MODULE EXISTS (WL-K7 Loader-Extraktion): the invoices loader used to be
 * duplicated verbatim in three call sites (`DatevExportPage`, `UmsatzsteuerPage`,
 * `GobdExportPanel`). WL-K7 hoists it here so the cross-mandant Advisor cockpit can
 * reuse the EXACT SAME loader — every export is parameterised by an explicit
 * `scopeUid` argument and never reads a shared/ambient scope. That parameterisation
 * is the structural guarantee against cross-tenant contamination in the Advisor
 * batch loop (K7-4/R-K7): mandant A's export can only ever carry mandant A's data
 * because `loadMandantDatevSource(A.scopeUid, …)` queries `userId == A.scopeUid`.
 *
 * PURE-vs-IO SEAM: the mappers are pure; only `loadInvoicesForUser` /
 * `loadMandantDatevSource` touch Firestore. No module-level mutable state — nothing
 * can leak between successive per-mandant calls.
 *
 * @module utils/datev/datevSource
 */

import { collection, query, where, getDocs, Timestamp } from 'firebase/firestore';

import { db } from '../../firebaseConfig';
import { Expense } from '../../types/expenseTypes';
import { InvoiceData } from '../../types/invoiceTypes';
import { eurosToCents } from '../moneyUtils';
import { reconstructPerRate } from '../invoicePerRate';
import type { DatevInvoiceInput, DatevExpenseInput, DatevTravelInput } from '../datevExport';
import type { TravelTrip } from '../../types/travelTypes';
import { mapSettledTripToDatevTravelInput } from '../travel/travelDatevMapper';

// ─── Firestore invoice doc shape (relevant subset) ──────────────────────────────

/** Roh-Form eines `invoices`-Dokuments, soweit für den DATEV-Export relevant. */
export interface DatevInvoiceDoc {
  id: string;
  userId: string;
  invoiceNumber?: string;
  invoiceDate?: string | Timestamp;
  buyerName?: string;
  totalAmount?: number;
  currency?: string;
  isCancellation?: boolean;
  isCancelled?: boolean;
  /**
   * Storage-Pfad der erzeugten ZUGFeRD-PDF, falls vorhanden (M7b-5d / P5). Präsenz
   * steuert `DatevInvoiceInput.hasPdf` → reine XRechnung-XML ohne PDF wird „ohne Beleg".
   */
  pdfPath?: string;
  /** Komplettes InvoiceData (einzige Quelle für Netto/USt-Split). */
  rawData?: Partial<InvoiceData>;
}

// ─── Date helpers ────────────────────────────────────────────────────────────────

function pad2(n: number): string {
  return String(n).padStart(2, '0');
}

function getTodayISO(): string {
  const d = new Date();
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
}

/** Extrahiert YYYY-MM-DD aus Timestamp/String/undefined. */
function toDateISO(value: string | Timestamp | null | undefined): string | null {
  if (!value) return null;
  if (typeof value === 'string') return value.slice(0, 10);
  if (value instanceof Timestamp) {
    const d = value.toDate();
    return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
  }
  return null;
}

/** Belegdatum-Priorität: top-level `invoiceDate` → `rawData.invoiceDate` → heute. */
function extractInvoiceDate(inv: DatevInvoiceDoc): string {
  const topLevel = toDateISO(inv.invoiceDate);
  if (topLevel) return topLevel;
  if (inv.rawData?.invoiceDate) return inv.rawData.invoiceDate.slice(0, 10);
  return getTodayISO();
}

// ─── Mappers (pure) ──────────────────────────────────────────────────────────────

/**
 * Mappt ein Firestore-`invoices`-Doc auf die entkoppelte `DatevInvoiceInput`-Form.
 *
 * `isSmallBusiness` MUSS der §19-Status DES BELEG-EIGNERS sein (Owner bzw. Mandant),
 * nicht der des aufrufenden Nutzers — die Advisor-Batch-Schleife reicht ihn deshalb
 * pro Mandant explizit herein (nie aus dem eigenen `sellerData` bleeden).
 */
export function mapInvoiceDocToDatevInput(
  inv: DatevInvoiceDoc,
  isSmallBusiness: boolean,
): DatevInvoiceInput {
  const perRate = reconstructPerRate(inv.rawData);
  const reconstructedGrossCents = perRate.reduce((sum, r) => sum + r.netCents + r.vatCents, 0);
  // BT-48 (Käufer-USt-IdNr.) — Gate für IGL-Mapping (Auflage 2). Defensiv aus rawData.buyer.
  const buyerVatId = inv.rawData?.buyer?.vatNumber;
  const buyerVatIdPresent = typeof buyerVatId === 'string' && buyerVatId.trim().length > 0;
  return {
    invoiceNumber: inv.invoiceNumber && inv.invoiceNumber.length > 0 ? inv.invoiceNumber : null,
    dateISO: extractInvoiceDate(inv),
    buyerName: inv.buyerName ?? '',
    isCancellation: inv.isCancellation === true,
    isSmallBusiness,
    perRate,
    reconstructedGrossCents,
    totalAmountCents: eurosToCents(inv.totalAmount ?? 0),
    currency: (inv.currency ?? 'EUR').toUpperCase(),
    buyerVatIdPresent,
    // M7b: Firestore-Doc-ID trägt die Beleglink-/Datei-Auflösung.
    docId: inv.id,
    // M7b-5d (P5): nur mit realer ZUGFeRD-PDF wird eine Datei erwartet; reine
    // XRechnung-XML ohne PDF ⇒ Buchung „ohne Beleg" (fail-closed, kein toter Link).
    hasPdf: typeof inv.pdfPath === 'string' && inv.pdfPath.length > 0,
  };
}

/** Mappt ein `Expense`-Dokument auf die entkoppelte `DatevExpenseInput`-Form. */
export function mapExpenseToDatevInput(e: Expense): DatevExpenseInput {
  return {
    invoiceNumber: e.invoiceNumber && e.invoiceNumber.length > 0 ? e.invoiceNumber : null,
    dateISO: (e.expenseDate ?? '').slice(0, 10),
    supplierName: e.supplierName ?? '',
    documentTypeCode: e.documentTypeCode ?? null, // defensiv (§3.2)
    netCents: e.netCents,
    vatOwedCents: e.vatOwedCents,
    vatDeductibleCents: e.vatDeductibleCents,
    grossCents: e.grossCents,
    vatRate: e.vatRate,
    taxScheme: e.taxScheme,
    vatDeductible: e.vatDeductible,
    skrAccount: e.skrAccount,
    status: e.status,
    completenessStatus: e.completenessStatus,
    nonEurCurrency: e.nonEurCurrency,
    // M7b: Firestore-Doc-ID + Beleg-Scan-Ref trägt die Beleglink-/Datei-Auflösung.
    docId: e.id,
    receiptStorageRef: e.receiptStorageRef ?? null,
    // Expense-Storno (Variante B, Privatentnahme): trägt das aufgelöste Gegenkonto
    // weiter an `processExpense` (datevExport.ts). `?? null` defensiv — Altbestand/
    // Nicht-Korrektur-Ausgaben tragen `privatentnahmeAccount` nicht → verhaltensneutral.
    gegenkontoOverride: e.privatentnahmeAccount ?? null,
  };
}

// ─── Invoice loader (SHARED — Muster OpenItemsPage/DatevExportPage) ──────────────

/**
 * Lädt alle `invoices`-Dokumente EINES Scopes (`userId == uid`). Verhaltens-identisch
 * zur früheren seiten-lokalen Kopie (WL-K7 R-K7-1). Generisch, damit
 * `UmsatzsteuerPage`/`GobdExportPanel` ihre eigene, engere Doc-Form beibehalten
 * können, ohne die Firestore-Query zu duplizieren.
 *
 * 🔴 Cross-Tenant-Isolation: `uid` wird IMMER explizit vom Aufrufer geliefert — der
 * Loader liest KEINEN ambienten Scope. Die Advisor-Batch-Schleife übergibt pro
 * Mandant dessen `scopeUid`, sodass niemals Daten eines anderen Mandanten geladen
 * werden können.
 *
 * @param uid Scope-uid (Owner-uid bzw. Mandanten-`scopeUid`).
 * @returns Roh-Dokumente `{ id, ...data }`; Fehler werden an den Aufrufer geworfen.
 */
export async function loadInvoicesForUser<T extends { id: string } = DatevInvoiceDoc>(
  uid: string,
): Promise<T[]> {
  const invoicesRef = collection(db, 'invoices');
  const invoicesQuery = query(invoicesRef, where('userId', '==', uid));
  const snap = await getDocs(invoicesQuery);
  return snap.docs.map((d) => ({ id: d.id, ...(d.data() as Omit<T, 'id'>) })) as T[];
}

// ─── Expense loader (SHARED — WL-K7 FIX v3, mirrors loadInvoicesForUser) ────────

/**
 * Lädt alle `expenses`-Dokumente EINES Scopes (`userId == scopeUid`) — OHNE den
 * belt-and-suspenders-Guard aus `expensesApi.getExpensesForUser` (dieser erzwingt
 * dort clientseitig `uid === getEffectiveScopeUid()`, was für den Advisor-Batch-Loop
 * — der den globalen Ambient-Scope NIE umschaltet — jeden Cross-Mandant-Read wirft).
 *
 * Root-Cause-Fix WL-K7 (v3, 2026-07-07): die Firestore-Rules erlauben diesen
 * Cross-Tenant-Read bereits (`expenses` steht seit M11-W4 auf `canReadInTenant` =
 * `isOwner || isActiveMember(doc.userId)`; ein aktiver `advisor` ist per Definition
 * `isActiveMember` jedes seiner Mandanten). Der Guard in `getExpensesForUser` ist für
 * den Owner-Pfad korrekt (dort gilt `scopeUid === effectiveScopeUid` immer), aber zu
 * eng für den Advisor-Pfad, der bewusst einen ANDEREN Scope liest. Deshalb ein
 * separater, guard-freier Loader statt den Owner-Guard aufzuweichen.
 *
 * **Bewusst OHNE `orderBy`** (kein Composite-Index-Bedarf; der DATEV-Export sortiert
 * die gemappten `DatevExpenseInput[]` selbst, falls nötig).
 *
 * 🔴 Cross-Tenant-Isolation: `scopeUid` wird IMMER explizit vom Aufrufer geliefert —
 * kein ambienter Scope, kein modul-globaler mutabler Zustand über Iterationen hinweg.
 *
 * @param scopeUid Scope-uid (Owner-uid bzw. Mandanten-`scopeUid`).
 * @returns Rohe `Expense[]`; Fehler werden an den Aufrufer geworfen (fail-closed —
 *          kein Teil-Export aus unvollständigen Daten).
 */
export async function loadExpensesForScope(scopeUid: string): Promise<Expense[]> {
  const expensesRef = collection(db, 'expenses');
  const expensesQuery = query(expensesRef, where('userId', '==', scopeUid));
  const snap = await getDocs(expensesQuery);
  return snap.docs.map((d) => ({ id: d.id, ...d.data() } as Expense));
}

// ─── Travel loader (RK-6, SHARED — Muster loadExpensesForScope) ─────────────────

/**
 * Lädt alle `travel_trips`-Dokumente EINES Scopes (`userId == scopeUid`) — guard-frei,
 * exakt das `loadExpensesForScope`-Muster (RK-6, §3.6). **Bewusst OHNE `orderBy`** (kein
 * Composite-Index-Bedarf, §4.1); der Aufrufer filtert/sortiert die gemappten Werte selbst.
 *
 * 🔴 Cross-Tenant-Isolation: `scopeUid` wird IMMER explizit vom Aufrufer geliefert — kein
 * ambienter Scope, kein modul-globaler mutabler Zustand (identisch zu invoices/expenses, WL-K7).
 *
 * @param scopeUid Scope-uid (Owner-uid bzw. Mandanten-`scopeUid`).
 * @returns Rohe `TravelTrip[]`; Fehler werden an den Aufrufer geworfen (fail-closed).
 */
export async function loadTravelTripsForScope(scopeUid: string): Promise<TravelTrip[]> {
  const tripsRef = collection(db, 'travel_trips');
  const tripsQuery = query(tripsRef, where('userId', '==', scopeUid));
  const snap = await getDocs(tripsQuery);
  return snap.docs.map((d) => ({ id: d.id, ...d.data() } as TravelTrip));
}

/**
 * Lädt Rechnungen + Ausgaben EINES Scopes und mappt sie auf die entkoppelten
 * DATEV-Eingabeformen. Die eine Stelle, an der eine vollständige DATEV-Datenquelle
 * pro Scope entsteht — von der Owner-Seite (`DatevExportPage`) UND der
 * Advisor-Batch-Schleife (`AdvisorDatevPage`) genutzt.
 *
 * Fail-closed: beide Quellen müssen laden (Promise.all), sonst wirft der Aufruf —
 * kein Teil-Export aus unvollständigen Daten (kein unvollständiger Buchungsstapel).
 *
 * RK-6 (additiv): das Rückgabe-Objekt trägt zusätzlich `travel` — die beleglosen Reise-Pauschalen
 * dieses Scopes. Aufgenommen werden nur Reisen MIT Settlement-Snapshot (`settlement != null`, also
 * `settled` und `cancelled`): settled-Reisen buchen, cancelled-Reisen erscheinen als sichtbarer
 * Ausschluss (`travelCancelled`, §6.3 — kein Umkehr-Pfad im MVP). `draft`-Reisen (kein Snapshot)
 * bleiben außen vor. Kein Composite-Index nötig (§4.1). Die eigentliche Buchungs-/Bucket-Entscheidung
 * trifft `processTravelAllowance` (`datevExport.ts`) — hier wird nur gemappt.
 *
 * @param scopeUid       Scope-uid des Beleg-Eigners (Owner bzw. Mandant).
 * @param isSmallBusiness §19-Status DIESES Scopes (nie aus fremdem `sellerData`).
 * @returns `{ invoices, expenses, travel }` als entkoppelte DATEV-Eingabeformen.
 */
export async function loadMandantDatevSource(
  scopeUid: string,
  isSmallBusiness: boolean,
): Promise<{
  invoices: DatevInvoiceInput[];
  expenses: DatevExpenseInput[];
  travel: DatevTravelInput[];
}> {
  const [rawInvoices, rawExpenses, rawTrips] = await Promise.all([
    loadInvoicesForUser<DatevInvoiceDoc>(scopeUid),
    loadExpensesForScope(scopeUid),
    loadTravelTripsForScope(scopeUid),
  ]);
  return {
    invoices: rawInvoices.map((inv) => mapInvoiceDocToDatevInput(inv, isSmallBusiness)),
    expenses: rawExpenses.map(mapExpenseToDatevInput),
    travel: rawTrips
      .filter((t) => t.settlement != null) // nur abgerechnete Reisen (settled/cancelled)
      .map(mapSettledTripToDatevTravelInput),
  };
}
